/**
 * Sites, editable.
 *
 * The routes behind this landed before the screen did, which meant the platform
 * could create a site and no human could. This is the other half.
 *
 * ── What this screen is for, and what it must not imply ──────────────────
 *
 * A site is where an asset physically is. Two of its fields are load-bearing in
 * ways that are not obvious from a form:
 *
 *   **timezone** renders every timestamp on that site's screens, and any future
 *   diurnal suppression ("offline at this hour on 24 of the last 30 nights")
 *   has to reason in it. The column is free text, so the server checks it
 *   against the runtime's own zone database — a wrong one does not fail, it
 *   renders wrong, for one customer, in a way nobody local notices.
 *
 *   **latitude/longitude** are drawn on the Map exactly as given. Reversed, a
 *   Brisbane site lands in the Indian Ocean, and the only half of that mistake
 *   that is detectable is that 153 is not a latitude. The server refuses both
 *   the swap and half a pair.
 *
 * Neither rule is re-implemented here. The server owns them and this screen
 * shows what it said — a second copy in the browser is a second thing to keep
 * in step, and the one that matters is the one the API enforces.
 *
 * ── Retire, not delete ───────────────────────────────────────────────────
 *
 * The button says Retire because that is what happens. Every observation, alert
 * and uptime figure is about an asset, and an asset belongs to a site; deleting
 * would either be refused by the foreign key or strand the context that makes
 * months of history readable. The server also refuses to retire a site that
 * still has live assets, and names them — that refusal is worth showing in
 * full rather than summarising.
 *
 * And it is reversible, as of 2026-08-22. Retirement used to be one-way: the
 * only route that set `retired_at` was DELETE, nothing cleared it, and a site
 * retired by a misclick came back only through hand-written SQL. A retired
 * site's drawer now carries Restore. A soft delete whose whole purpose is that
 * nothing is destroyed should not be the one action on this screen that cannot
 * be undone.
 *
 * ── Shape ────────────────────────────────────────────────────────────────
 *
 * A read-only list; a row opens a drawer with the form. The list used to be a
 * spreadsheet — an input in every cell and a Save per row — which put ten
 * primary-looking buttons on one screen and made the list unsortable. The
 * drawer is where the edit lives, with the server's refusal beside the field
 * it is about rather than in a banner above a table of thirty rows.
 */

import { Download, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, ApiError } from './api.ts';
import {
  Banner,
  Button,
  DataTable,
  Drawer,
  Empty,
  Field,
  FilterChip,
  Loading,
  Pill,
  Section,
  Toolbar,
  type ColumnDef,
  DownloadLink,
} from './components/index.ts';

interface Site {
  id: string;
  code: string | null;
  name: string;
  timezone: string;
  address: string | null;
  locality: string | null;
  region: string | null;
  postcode: string | null;
  country: string;
  latitude: number | null;
  longitude: number | null;
  retired_at: string | null;
}

interface SitesResponse {
  sites: Site[];
}

/** The form, as strings — which is what inputs give and the API accepts. */
interface Draft {
  name: string;
  code: string;
  timezone: string;
  locality: string;
  region: string;
  postcode: string;
  address: string;
  country: string;
  latitude: string;
  longitude: string;
}

const EMPTY: Draft = {
  name: '',
  code: '',
  // The fleet is Australian and every seeded site is Brisbane. A default that
  // is right most of the time beats an empty box that must be filled correctly
  // to proceed, and the server refuses anything it cannot format in.
  timezone: 'Australia/Brisbane',
  locality: '',
  region: '',
  postcode: '',
  address: '',
  country: 'AU',
  latitude: '',
  longitude: '',
};

const draftOf = (site: Site): Draft => ({
  name: site.name,
  code: site.code ?? '',
  timezone: site.timezone,
  locality: site.locality ?? '',
  region: site.region ?? '',
  postcode: site.postcode ?? '',
  address: site.address ?? '',
  country: site.country,
  // `?? null` before the null test: a field the list route does not return is
  // undefined, and String(undefined) is the text "undefined" — which is what
  // these inputs showed until GET /api/sites returned the whole record.
  latitude: (site.latitude ?? null) === null ? '' : String(site.latitude),
  longitude: (site.longitude ?? null) === null ? '' : String(site.longitude),
});

const position = (site: Site): string =>
  (site.latitude ?? null) === null || (site.longitude ?? null) === null
    ? '—'
    : `${site.latitude}, ${site.longitude}`;

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));

/* The hints, once, so the create and edit forms cannot drift apart. */
const HINT = {
  code: 'The business key on a job sheet — CK1048. Optional; compared case-insensitively, so CK1048 and ck1048 cannot become two sites.',
  timezone:
    'An IANA name. Renders every timestamp on this site’s screens, so a wrong one does not fail — it reads wrong, for this customer only.',
  latitude: 'Both or neither — one without the other is not a position.',
  longitude:
    'Brisbane is about -27.47, 153.02. Reversed, the latitude would be 153, which is why a swap is refused rather than drawn.',
} as const;

export function SitesEditor({ canExport = false }: { canExport?: boolean } = {}): React.JSX.Element {
  const [data, setData] = useState<SitesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRetired, setShowRetired] = useState(false);
  const [selected, setSelected] = useState<Site | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await api.get<SitesResponse>(
        `/api/sites${showRetired ? '?includeRetired=1' : ''}`,
      );
      setData(next);
      setError(null);
    } catch (err) {
      setError(failure(err));
    }
  }, [showRetired]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo<ColumnDef<Site, unknown>[]>(
    () => [
      {
        id: 'code',
        header: 'Code',
        accessorFn: (s) => s.code ?? '',
        cell: ({ getValue }) => <span className="mono">{(getValue() as string) || '—'}</span>,
        size: 110,
      },
      { id: 'name', header: 'Name', accessorKey: 'name' },
      {
        id: 'locality',
        header: 'Locality',
        accessorFn: (s) => s.locality ?? '',
        cell: ({ getValue }) => <span className="muted">{(getValue() as string) || '—'}</span>,
      },
      { id: 'timezone', header: 'Timezone', accessorKey: 'timezone' },
      {
        id: 'position',
        header: 'Position',
        accessorFn: position,
        enableSorting: false,
        cell: ({ getValue }) => <span className="mono small muted">{getValue() as string}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (s) => (s.retired_at === null ? 'active' : 'retired'),
        cell: ({ row }) =>
          row.original.retired_at === null ? (
            <Pill state="ok">active</Pill>
          ) : (
            <Pill
              state="retired"
              reason={`Retired ${new Date(row.original.retired_at).toLocaleDateString()}. Open to restore.`}
            >
              retired
            </Pill>
          ),
        size: 96,
      },
    ],
    [],
  );

  if (error !== null && data === null) return <Banner tone="fault">{error}</Banner>;
  if (data === null) return <Loading rows={6} cols={6} label="Loading sites" />;

  return (
    <>
      {error !== null && <Banner tone="fault">{error}</Banner>}

      <Toolbar
        end={
          <>
            {canExport && (
              <DownloadLink href="/api/export/sites.csv" icon={<Download size={14} />}>
                Export CSV
              </DownloadLink>
            )}
            <Button variant="primary" icon={<Plus size={14} />} onClick={() => setCreating(true)}>
              New site
            </Button>
          </>
        }
      >
        <FilterChip active={showRetired} onClick={() => setShowRetired((v) => !v)}>
          Show retired
        </FilterChip>
      </Toolbar>

      <Section flush title="Sites" summary="Where assets physically are. Open a row to edit it.">
        <DataTable
          data={data.sites}
          columns={columns}
          getRowId={(s) => s.id}
          initialSort={[{ id: 'code', desc: false }]}
          onRowClick={setSelected}
          rowClass={(s) => (s.retired_at === null ? undefined : 'row-muted')}
          caption="Sites"
          empty={
            <Empty
              title="No sites yet"
              body="Every asset belongs to a site. Add the first one to give the fleet somewhere to be."
            />
          }
        />
      </Section>

      {selected !== null && (
        <SiteDrawer
          site={selected}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            void load();
          }}
        />
      )}

      {creating && (
        <NewSiteDrawer
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
        />
      )}
    </>
  );
}

/** The fields the two forms share. `disabled` is the retired case. */
function SiteFields({
  draft,
  onChange,
  prefix,
  disabled,
}: {
  draft: Draft;
  onChange: (patch: Partial<Draft>) => void;
  prefix: string;
  disabled: boolean;
}): React.JSX.Element {
  const id = (name: string): string => `${prefix}-${name}`;
  return (
    <>
      <Field id={id('name')} label="Name">
        <input
          id={id('name')}
          value={draft.name}
          onChange={(e) => onChange({ name: e.target.value })}
          disabled={disabled}
        />
      </Field>

      <Field id={id('code')} label="Code" hint={HINT.code}>
        <input
          id={id('code')}
          value={draft.code}
          onChange={(e) => onChange({ code: e.target.value })}
          disabled={disabled}
        />
      </Field>

      <Field id={id('timezone')} label="Timezone" hint={HINT.timezone}>
        <input
          id={id('timezone')}
          value={draft.timezone}
          onChange={(e) => onChange({ timezone: e.target.value })}
          disabled={disabled}
        />
      </Field>

      <Field id={id('locality')} label="Locality">
        <input
          id={id('locality')}
          value={draft.locality}
          onChange={(e) => onChange({ locality: e.target.value })}
          disabled={disabled}
        />
      </Field>

      <Field id={id('latitude')} label="Latitude" hint={HINT.latitude}>
        <input
          id={id('latitude')}
          value={draft.latitude}
          onChange={(e) => onChange({ latitude: e.target.value })}
          disabled={disabled}
          inputMode="decimal"
        />
      </Field>

      <Field id={id('longitude')} label="Longitude" hint={HINT.longitude}>
        <input
          id={id('longitude')}
          value={draft.longitude}
          onChange={(e) => onChange({ longitude: e.target.value })}
          disabled={disabled}
          inputMode="decimal"
        />
      </Field>
    </>
  );
}

/**
 * One site's form. The drawer is mounted per selection, so the draft starts
 * from the row that was clicked and is thrown away on close — there is no
 * per-row draft cache to fall out of step with the list.
 */
function SiteDrawer({
  site,
  onClose,
  onChanged,
}: {
  site: Site;
  onClose: () => void;
  onChanged: () => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState<Draft>(() => draftOf(site));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const retired = site.retired_at !== null;

  const run = async (request: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await request();
      onChanged();
    } catch (err) {
      // The server's refusals are the useful part — a bad timezone or half a
      // coordinate pair each come back as a sentence somebody can act on, and
      // a 409 on retire names the assets still at this site, in full.
      // Truncating it to "in use" would send somebody hunting through the
      // fleet for which ones — the whole reason the API lists them.
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  const save = (): Promise<void> => run(() => api.put(`/api/sites/${site.id}`, draft));

  const retire = (): Promise<void> => run(() => api.del(`/api/sites/${site.id}`));

  /**
   * Bring a retired site back.
   *
   * Sends the site's own stored values rather than the draft: the inputs are
   * disabled while a site is retired, so restoring must not silently apply an
   * edit nobody asked for. `retired: false` is the only thing this changes.
   */
  const restore = (): Promise<void> =>
    run(() =>
      api.put(`/api/sites/${site.id}`, {
        name: site.name,
        code: site.code,
        timezone: site.timezone,
        country: site.country,
        address: site.address,
        locality: site.locality,
        region: site.region,
        postcode: site.postcode,
        latitude: site.latitude,
        longitude: site.longitude,
        retired: false,
      }),
    );

  return (
    <Drawer
      open
      onClose={onClose}
      title={site.name}
      subtitle={
        <>
          {site.code ?? 'no code'}
          {retired && (
            <>
              {' · '}
              <Pill state="retired">retired</Pill>
            </>
          )}
        </>
      }
    >
      <div className="form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {retired && (
          <Banner tone="info">
            Retired sites are read-only. Its code was never released, so nothing can have taken
            it — restoring brings it back exactly as it was.
          </Banner>
        )}

        <SiteFields
          draft={draft}
          onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
          prefix={`site-${site.id}`}
          disabled={retired || busy}
        />

        <div className="row">
          {retired ? (
            <Button busy={busy} onClick={() => void restore()}>
              Restore
            </Button>
          ) : (
            <Button variant="primary" busy={busy} onClick={() => void save()}>
              Save
            </Button>
          )}
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          {!retired && (
            <>
              <span className="spacer" />
              <Button variant="danger" busy={busy} onClick={() => void retire()}>
                Retire
              </Button>
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
}

function NewSiteDrawer({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/sites', draft);
      onCreated();
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open onClose={onClose} title="New site" subtitle="Where a group of assets lives">
      <div className="form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}

        <SiteFields
          draft={draft}
          onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
          prefix="new-site"
          disabled={busy}
        />

        <div className="row">
          <Button variant="primary" busy={busy} onClick={() => void create()}>
            Create site
          </Button>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
